/**
 * Payment-setup status display (ADR 0040 gate semantics, ADR 0051 vocabulary),
 * shared by Get Started and Settings → Payout account. Five states — the
 * organizer sees payments/payout language, never Stripe internals:
 *   1. no account            → hint + "Set Up Payments"
 *   2. account, items due    → amber "Payment setup incomplete" + humanized
 *                              outstanding items + "Continue payment setup"
 *   3. account, nothing due,
 *      charges not enabled   → "Verification in progress"
 *   4. charges enabled       → green "Payments enabled" (+ bank last4)
 *   5. charges enabled but
 *      payouts disabled      → "Payments enabled — payouts paused" (+ reason)
 * "Enabled" means charges_enabled — an account merely existing is NOT ready.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { CreditCard, CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';
import type { StripeConnectStatus } from '@/hooks/useStripeConnectStatus';

interface StripeConnectStatusCardProps {
  status: StripeConnectStatus | null;
  loading?: boolean;
  onConnect: () => void;
  notConnectedHint?: React.ReactNode;
}

const REQUIREMENT_LABELS: Record<string, string> = {
  external_account: 'Bank account for payouts',
  'individual.verification.document': 'Verification document',
  'tos_acceptance.date': 'Payment terms of service',
  'tos_acceptance.ip': 'Payment terms of service',
  'business_profile.url': 'Business website',
  'business_profile.mcc': 'Business category',
};

// Human label for a requirement key ("individual.verification.document" →
// "Verification document"). Only well-known keys get bespoke copy; the
// fallback prettifies the last dotted segment so new keys stay readable.
export const humanizeRequirement = (key: string): string => {
  if (REQUIREMENT_LABELS[key]) return REQUIREMENT_LABELS[key];
  const last = key.split('.').pop() || key;
  const words = last.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const MAX_SHOWN_REQUIREMENTS = 3;

export default function StripeConnectStatusCard({
  status, loading, onConnect, notConnectedHint,
}: StripeConnectStatusCardProps) {
  // While the first status fetch is in flight, show a neutral skeleton — never
  // flash "not configured" at an org whose charges are enabled.
  if (loading && !status) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="stripe-status-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking payment status…
      </div>
    );
  }

  if (!status?.hasAccount) {
    return (
      <div className="space-y-2" data-testid="stripe-status-none">
        {notConnectedHint && <p className="text-sm text-muted-foreground">{notConnectedHint}</p>}
        <Button variant="outline" onClick={onConnect}>
          <CreditCard className="mr-2 h-4 w-4" /> Set Up Payments
        </Button>
      </div>
    );
  }

  if (status.error === 'unavailable') {
    return (
      <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning" data-testid="stripe-status-unavailable">
        We couldn&apos;t check your payment status. Try again shortly.
      </div>
    );
  }

  if (!status.chargesEnabled) {
    const due = status.requirementsDue || [];
    const shown = due.slice(0, MAX_SHOWN_REQUIREMENTS);
    return (
      <div className="space-y-2 rounded-lg border border-warning/30 bg-warning/10 p-4" data-testid="stripe-status-in-progress">
        {due.length > 0 ? (
          <>
            <div className="flex items-center gap-2 text-sm font-medium text-warning">
              <TriangleAlert className="h-4 w-4" /> Payment setup incomplete. Real payments aren&apos;t ready yet.
            </div>
            <div className="text-xs text-warning">
              {due.length} {due.length === 1 ? 'item' : 'items'} still needed:
              <ul className="mt-1 list-disc pl-5">
                {shown.map((r) => <li key={r}>{humanizeRequirement(r)}</li>)}
                {due.length > shown.length && <li>+{due.length - shown.length} more</li>}
              </ul>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm font-medium text-warning">
            <TriangleAlert className="h-4 w-4" /> Verification in progress (usually takes a few minutes).
          </div>
        )}
        <Button size="sm" variant="outline" onClick={onConnect}>
          <CreditCard className="mr-2 h-4 w-4" /> Continue payment setup
        </Button>
      </div>
    );
  }

  const bank = status.externalAccount;
  return (
    <div className="space-y-1" data-testid="stripe-status-ready">
      <div className="flex items-center gap-2 text-sm text-success">
        <CheckCircle2 className="h-4 w-4" />
        {status.payoutsEnabled === false ? 'Payments enabled, payouts paused.' : 'Payments enabled.'}
      </div>
      {bank?.last4 && (
        <p className="pl-6 text-xs text-muted-foreground" data-testid="stripe-status-bank">
          {bank.bankName ? `${bank.bankName} ` : 'Bank '}ending •••• {bank.last4}
        </p>
      )}
      {status.payoutsEnabled === false && (
        <p className="pl-6 text-xs text-muted-foreground">
          {status.disabledReason
            ? `Payouts are paused (${humanizeRequirement(status.disabledReason)}). Charges still work; use Continue payment setup to resolve it.`
            : 'Payouts are still being enabled. Charges work, transfers to your bank follow shortly.'}
        </p>
      )}
    </div>
  );
}
